/**
 * One transcript message's TEXT, rendered by role. Visual language matches
 * legacy MessageList: BOTH roles are full-width (not floated bubbles) — user = a
 * full-width rounded block on #303030, assistant = full-width borderless.
 * system = muted note.
 *
 * Tool calls do not reach here: `buildThreadRows` lifts them out into their own
 * rows so a run of them can be grouped across messages, and tool_result messages
 * fold into the call they answer.
 *
 * Attachments (RFC 0288) DO reach here, as their own block type. They are not
 * routed through Markdown: Markdown has no image rule, and its link rule would
 * match the `[alt](url)` tail of an `![alt](url)` and render a stray `!`.
 */

import type { TranscriptMessage } from '@mod/core/model'
import { formatBytes, isImage } from '@mod/core/model'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useOptionalTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { useAttachmentUrl } from './attachments'
import { Markdown } from './Markdown'

export function Message({ message }: { message: TranscriptMessage }) {
  // Attachments are lifted OUT of the message body and drawn above it.
  //
  // Inside it they inherited the bubble: a full-width block with padding and a
  // background, which framed every picture in a wide slab of grey. A picture is
  // its own thing, so it gets no container at all, and the words that came with
  // it read as a caption underneath.
  const attachments = message.content.filter((b) => b.type === 'attachment')
  const text = message.content.filter(
    (b) =>
      b.type !== 'attachment' &&
      b.type !== 'view_attachment' &&
      b.type !== 'tool_use',
  )
  const blocks = text.map((block, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: append-only ordered blocks
    <Markdown key={i} text={block.text} />
  ))
  const hasText = text.some((b) => b.text.trim().length > 0)

  if (message.role === 'system') {
    return (
      <div className="px-2 py-0.5 text-muted-foreground/70 text-xs italic">
        {blocks}
      </div>
    )
  }

  // Full-width for both roles (matching legacy MessageList): user = rounded
  // #303030 block, assistant = borderless.
  const isUser = message.role === 'user'
  const queued = isUser && message.runStatus === 'queued'
  const body = hasText ? (
    <div
      className={`w-full rounded-2xl ${isUser ? 'px-3 py-2 text-white' : 'px-2 py-1.5 text-foreground/90'}`}
      style={isUser ? { backgroundColor: '#303030' } : undefined}
    >
      {blocks}
    </div>
  ) : null

  const status = queued ? (
    <div className="px-3 text-muted-foreground text-xs">queued</div>
  ) : null

  if (attachments.length === 0) {
    if (!status) return body
    return (
      <div className="flex w-full flex-col items-start gap-1">
        {body}
        {status}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      {/* Wrapped, so several pictures sit side by side and spill onto a second
          row rather than making a column as tall as the thread. */}
      <div className="flex flex-wrap items-end gap-1.5">
        {attachments.map((block) => (
          <Attachment key={block.docId} block={block} />
        ))}
      </div>
      {body}
      {status}
    </div>
  )
}

/**
 * An image at its own size, over everything.
 *
 * A portal to document.body, because the thread's rows are inside a scroll
 * container with its own stacking context: rendered in place, a fixed overlay
 * would be clipped by it.
 */
function Lightbox({
  src,
  alt,
  onClose,
}: {
  src: string
  alt: string
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Rung 0 of the Esc ladder (RFC 0266 §3d): the topmost thing closes, and
      // stops here. Without this the same Esc would also blur the composer or
      // close the file underneath.
      e.preventDefault()
      e.stopPropagation()
      onClose()
    }
    // Capture phase, so this runs before the composer's and the shell's
    // handlers rather than after whatever they decide to do.
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return createPortal(
    // A real button, not a div with onClick: the backdrop IS the dismiss
    // affordance, so making it one gives it focus and Enter/Space for free
    // rather than needing two a11y rules suppressed.
    <button
      type="button"
      onClick={onClose}
      aria-label={`Close ${alt}`}
      className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/80 p-8"
    >
      <img
        src={src}
        alt={alt}
        // Its own size, never upscaled past it, and never past the window.
        className="max-h-full max-w-full rounded-lg object-contain"
      />
    </button>,
    document.body,
  )
}

/**
 * One attachment, resolved from its document.
 *
 * An image that has not synced to this peer yet falls back to the same named
 * chip a PDF gets, rather than a broken image: the pointer is on the log, so we
 * know its name and size even when the bytes have not arrived.
 */
function Attachment({
  block,
}: {
  block: Extract<TranscriptMessage['content'][number], { type: 'attachment' }>
}) {
  // Optional on purpose: Thread and Message render in tests and in slots with
  // no provider, and an attachment that cannot resolve should degrade to a chip
  // rather than take the whole transcript down with it.
  const boot = useOptionalTabRepoBoot()
  const url = useAttachmentUrl(boot?.repo, block.docId)
  const [full, setFull] = useState(false)

  if (isImage(block.mimeType) && url) {
    return (
      <>
        <button
          type="button"
          onClick={() => setFull(true)}
          title={`${block.name} — click to view full size`}
          // `flex` not `block`: an inline-block button adds a baseline gap
          // under the image, which reads as padding the picture does not have.
          className="flex cursor-zoom-in rounded-lg"
        >
          <img
            src={url}
            alt={block.name}
            // aspect-ratio, NOT width/height attributes: the attributes size the
            // box to the stored dimensions, and with object-contain the image
            // then letterboxes inside it, which is the blank margin this had.
            // Here the box hugs the image and the ratio still reserves the space
            // before the bytes decode, so the thread does not jump as they load.
            style={
              block.width && block.height
                ? { aspectRatio: `${block.width} / ${block.height}` }
                : undefined
            }
            className="h-auto max-h-60 w-auto max-w-full rounded-lg"
          />
        </button>
        {full && (
          <Lightbox src={url} alt={block.name} onClose={() => setFull(false)} />
        )}
      </>
    )
  }

  return (
    <span className="flex w-fit max-w-full items-center gap-1.5 rounded-lg border border-border/60 bg-black/10 px-2 py-1.5">
      <span className="truncate text-xs">{block.name}</span>
      <span className="shrink-0 text-[10px] opacity-60">
        {formatBytes(block.bytes)}
      </span>
    </span>
  )
}
